import React from "react";
import { Img, staticFile } from "remotion";
import { theme } from "../brand";

type Variant = "full" | "icon";

type Props = {
  variant?: Variant;
  /** Rendered pixel width. The SVG keeps its aspect ratio. */
  width: number;
  color?: string;
  style?: React.CSSProperties;
};

/**
 * Frak brand mark.
 *
 *   - `full`  = the official SVG shipped in /public/logo-frak.svg
 *               (icon + wordmark, blue #0043ef).
 *   - `icon`  = just the three slanted bars, inline SVG so we can recolor
 *               them frame-by-frame (used in Scene 2 when the logo bursts
 *               from the bottom and emits the wave).
 *
 * Keep consumers free of hardcoded colors — always pass a brand token.
 */
export const FrakLogo: React.FC<Props> = ({
  variant = "full",
  width,
  color,
  style,
}) => {
  if (variant === "full") {
    // Logo aspect ratio = 73:28 (from the SVG viewBox).
    const height = (width * 28) / 73;
    return (
      <Img
        src={staticFile("/logo-frak.svg")}
        style={{
          width,
          height,
          // Allow tinting via CSS filter if a color is provided. The SVG
          // has a hardcoded fill that we can't override without editing,
          // so we leave it as-is for the "full" variant and rely on its
          // native #0043EF brand blue.
          ...style,
        }}
      />
    );
  }

  // Inline icon-only variant — three parallelograms. Redrawn from the
  // source SVG (viewBox 0 0 28 28), so we can animate its fill.
  const iconColor = color ?? theme.brand.colors.primary[600];
  return (
    <svg
      width={width}
      height={width}
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <path
        d="M9.59332 21.7692L0.358405 18.6439C0.182592 18.5842 0 18.7153 0 18.901V24.603C0 24.7196 0.0741217 24.8226 0.1844 24.8601L9.41932 27.9854C9.59513 28.0451 9.77772 27.914 9.77772 27.7283V22.0263C9.77772 21.9097 9.7036 21.8067 9.59332 21.7692Z"
        fill={iconColor}
      />
      <path
        d="M19.1993 10.8344L9.96212 13.9597C9.85184 13.9972 9.77772 14.1003 9.77772 14.2169V19.9188C9.77772 20.1046 9.96031 20.2357 10.1361 20.176L19.3733 17.0507C19.4836 17.0132 19.5577 16.9101 19.5577 16.7935V11.0916C19.5577 10.9058 19.3756 10.7748 19.1993 10.8344Z"
        fill={iconColor}
      />
      <path
        d="M27.8738 0.00860862L12.4095 4.98922C12.2974 5.02537 12.2215 5.12978 12.2215 5.24774V11.0898C12.2215 11.2059 12.3367 11.2882 12.4488 11.252L27.9131 6.27143C28.0252 6.23527 28.1016 6.13087 28.1016 6.01291V0.170862C28.1011 0.0547085 27.9859 -0.0275482 27.8738 0.00860862Z"
        fill={iconColor}
      />
    </svg>
  );
};
